import datapoints from './datapoints';
import { getOWAConnectedAccounts } from 'owa-accounts-store';
import { returnTopExecutingActionDatapoint } from 'owa-analytics';
import { createLegacyActionWithDatapoint } from 'owa-analytics-actions';
import initializeCalendarsDataForModule from 'owa-calendar-bootstrap-utils/lib/initializeCalendarsDataForModule';
import { initializeCalendarLayout } from 'owa-calendar-layout';
import { goToTodayOrchestrator } from 'owa-calendar-module-commanding-orchestration/lib/orchestrators/goToTodayOrchestrator';
import { initializeSurfaceOptions } from 'owa-calendar-options-store';
import { refreshCalendarStoreWithCalendarCacheData } from 'owa-calendar-surface-select-calendar';
import { getUserMailboxInfo } from 'owa-client-ids';
import { timestamp } from 'owa-datetime';
import { isFeatureEnabled } from 'owa-feature-flags';
import { HeaderHeight } from 'owa-flex-pane/lib/components/FlexPaneVariables';
import { isHostAppFeatureEnabled } from 'owa-hostapp-feature-flags';
import { initializeTimePanelConfig } from 'owa-time-panel-config';
import { canUseToDoFeatures } from 'owa-todo-utils/lib/utils/moduleAccessUtils';
import { initializeCommandingViewMode } from 'owa-command-ribbon-store';
import { getPrimeBootSettingsOptionsValues } from 'owa-session-store';

// TODO: VSO #89735 Audit boundary between surface and canvas

/**
 * Importing owa-calendar-surface-view-store to register orchestrators which are required by the actions called in initializeState
 */
import 'owa-calendar-surface-view-store';

export default createLegacyActionWithDatapoint(
    'initializeState',
    datapoints.LoadCalendarModuleE2E
)(function initializeState() {
    // The SessionElapseTime added to action datapoints by framework is in sec (rather than msec), however in order
    // to measure the total load time for calendar, we need the exact time in msec so that we can add it to E2ETimeElapsed.
    // Hence, adding SessionElapsedTime in msecs to this action datapoint
    let topExecutingActionDatapoint = returnTopExecutingActionDatapoint();
    if (topExecutingActionDatapoint) {
        topExecutingActionDatapoint.addCustomData({
            SessionElapsedTime: window.performance
                ? timestamp() - window.performance.timing.fetchStart
                : undefined,
        });
    }

    // 1. initialize module layout
    initializeCalendarLayout();

    // 2. initialize commanding view mode (e.g. commandbar/ribbon view mode)
    const options = getPrimeBootSettingsOptionsValues();
    initializeCommandingViewMode(options);

    // 3.initialize surface config
    initializeSurfaceOptions();

    // 4. update the surface store with the correct date
    // TODO: VSO #88707 Separate module vs surface concerns in Calendar module
    // Long-standing limitation with running date-based operations within OWA bootstrap, would be great to eventually
    // eliminate the need for this in a way that doesn't impact surface render latency
    goToTodayOrchestrator.handler({});

    // 5. customize Time Panel config for Calendar module
    initializeTimePanelConfig({
        abovePanelHeight: HeaderHeight,
        initialPanelView: canUseToDoFeatures() ? 'Tasks' : 'Calendar', // default to 'Tasks' for better companion experience
        initialCalendarView: 'Agenda',
        areFREsDisabled: false,
        isFullComposeEnabled: true, // leverage full compose form provided by module over popouts
        isClosePaneButtonEnabled: false,
        isTasksViewDisabled: false,
    });

    // 6. initialize calendars data
    initializeCalendarsDataForModule();

    // TODO: VSO #110443 Fix legacy calendar module architecture to support module-switching without hacks
    //
    // This call is gated by feature flight for several reasons:
    // 1. the change is motivated by module-switching feature
    // 2. scoping the change to feature flight allows for increased bake time and reduced risk of regression
    // 3. this is a "hack fix" that covers up long-term, underlying issues with our calendar architecture
    //      - legacy calendar module code is written in "Satchel waterfall" manner that is not resilient to changes
    //        in registration order, unlike modern code that is scenario-agnostic, reusable, and written in an
    //        action-up / data-down manner that puts more emphasis on pure (re)derivation of state separately from
    //        initialization flows
    //      - we need to improve our legacy calendar architecture to be re-callable OR (more preferably) migrate to /
    //        merge with modern architecture, which will also yield benefits for upcoming Monarch/GraphQL work
    //
    // We should NOT rollout on web beyond MSIT ring without further validation and thumbs-up by Calendar team
    // or else putting the proper fixes in place to remove this workaround
    if (isHostAppFeatureEnabled('moduleSwitch') || isFeatureEnabled('fwk-module-switch')) {
        getAllAccounts().forEach(refreshCalendarStoreWithCalendarCacheData);
    }
});

function getAllAccounts(): string[] {
    return [
        getUserMailboxInfo().userIdentity,
        ...getOWAConnectedAccounts().map(account => account.userIdentity),
    ];
}
